import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  MdCalendarToday,
  MdCheckCircle,
  MdDescription,
  MdHelp,
  MdLocationOn,
  MdNotifications,
  MdPayments,
} from 'react-icons/md';
import logoWhite from 'assets/img/layout/logoWhite.png';
import TutorialOverlay from 'components/tutorial/TutorialOverlay';
import { AuthContext } from 'contexts/AuthContext';
import { isInstructorOnboardingComplete } from 'utils/instructorOnboarding';

export const getTourStorageKey = (userDocId) => `hasSeenInstructorPortalWalkthrough:${userDocId}`;
export const MOBILE_TOUR_BREAKPOINT_PX = 768;
export const isMobileTourViewport = () => (
  typeof window !== 'undefined' && window.innerWidth < MOBILE_TOUR_BREAKPOINT_PX
);

const dashboardPath = '/admin/default';
const calendarPath = '/admin/calendar';
const opportunitiesPath = '/admin/opportunities';
const payoutsPath = '/admin/payouts';
const documentsPath = '/admin/documents';
const profilePath = '/admin/profile';

const FeatureItem = ({ icon, title, description, iconBg, brandColor }) => {
  const textColor = useColorModeValue('gray.800', 'white');
  const textColorSecondary = useColorModeValue('gray.600', 'gray.400');

  return (
    <Flex align="start" gap={3}>
      <Box bg={iconBg} p={2} borderRadius="8px">
        <Icon as={icon} w={5} h={5} color={brandColor} />
      </Box>
      <Box flex="1">
        <Text fontSize="sm" fontWeight="600" color={textColor} mb={1}>
          {title}
        </Text>
        <Text fontSize="sm" color={textColorSecondary}>
          {description}
        </Text>
      </Box>
    </Flex>
  );
};

export default function InstructorPortalTour() {
  const history = useHistory();
  const location = useLocation();
  const { userData, userDocId, stripeSetupRequired } = useContext(AuthContext);

  const [showWelcome, setShowWelcome] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [currentPage, setCurrentPage] = useState('welcome');
  const [isMobileViewport, setIsMobileViewport] = useState(() => isMobileTourViewport());

  const bg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const textColorSecondary = useColorModeValue('gray.600', 'gray.400');
  const brandColor = useColorModeValue('brand.500', 'brand.400');
  const iconBg = useColorModeValue('brand.50', 'whiteAlpha.100');

  const onboardingComplete = Boolean(userDocId && userData && isInstructorOnboardingComplete(userData));

  const navigateToStepPath = useCallback((path) => {
    if (!path || location.pathname === path) return;

    if (path === dashboardPath) {
      history.push('/admin/default');
    } else if (path === calendarPath) {
      history.push('/admin/calendar');
    } else if (path === opportunitiesPath) {
      history.push('/admin/opportunities');
    } else if (path === payoutsPath) {
      history.push('/admin/payouts');
    } else if (path === documentsPath) {
      history.push('/admin/documents');
    } else if (path === profilePath) {
      history.push('/admin/profile');
    }
  }, [history, location.pathname]);

  const broadcastTourTarget = useCallback((targetId = '') => {
    window.dispatchEvent(new CustomEvent('instructorPortalTourStepChange', {
      detail: { targetId },
    }));
  }, []);

  const getAllSteps = useCallback(() => {
    const steps = [
      {
        path: dashboardPath,
        targetId: 'nav-dashboard',
        title: 'Main Dashboard',
        description: 'Start here for the big picture: classes needing attention, upcoming classes, nearby opportunities, and a quick calendar.',
      },
      {
        path: dashboardPath,
        targetId: 'needs-attention-widget',
        title: 'Needs Attention',
        description: 'If a past class needs a completion form, it appears here. If this area is empty, you are caught up.',
      },
      {
        path: dashboardPath,
        targetId: 'upcoming-classes-widget',
        title: 'Upcoming Classes',
        description: 'Assigned classes show here. If you do not have any yet, this section will let you know and stay ready for the next assignment.',
      },
      {
        path: dashboardPath,
        targetId: 'opportunities-widget',
        title: 'Nearby Opportunities',
        description: 'Open classes in your travel area appear here when available. If none are nearby, try checking the Opportunities page later.',
      },
      {
        path: dashboardPath,
        targetId: 'calendar-view-widget',
        title: 'Quick Calendar',
        description: 'This gives you a fast calendar snapshot of assigned classes without leaving the dashboard.',
      },
      {
        path: dashboardPath,
        targetId: 'nav-calendar',
        title: 'Calendar',
        description: 'Open the full calendar when you want a larger view of your assigned class schedule.',
      },
      {
        path: calendarPath,
        targetId: 'calendar-full-widget',
        title: 'Full Calendar',
        description: 'Your assigned classes appear here. If it is empty, claimed or assigned classes will populate this calendar later.',
      },
      {
        path: dashboardPath,
        targetId: 'nav-opportunities',
        title: 'Opportunities',
        description: 'Browse open classes nearby and claim the ones that fit your schedule.',
      },
      {
        path: opportunitiesPath,
        targetId: 'opportunities-map-widget',
        title: 'Opportunity Map',
        description: 'The map centers around your location and shows available class locations when there are matches.',
      },
      {
        path: opportunitiesPath,
        targetId: 'opportunities-list-widget',
        title: 'Opportunity List',
        description: 'Cards show class details and claim actions. Empty states are normal when no classes match your current travel range.',
      },
      {
        path: dashboardPath,
        targetId: 'nav-payouts',
        title: 'Payouts',
        description: 'Review your payout setup and class payment history.',
      },
      {
        path: payoutsPath,
        targetId: 'payout-summary-widget',
        title: 'Payment Method',
        description: 'See your Stripe payout method, available balance, pending balance, and all-time transfer volume when Stripe details are available.',
      },
      {
        path: payoutsPath,
        targetId: 'payout-history-widget',
        title: 'Payout History',
        description: 'Completed class payouts appear here with class and facility details. It may be empty if you have not been paid yet.',
      },
      {
        path: dashboardPath,
        targetId: 'nav-documents',
        title: 'Documents',
        description: 'Upload insurance, certifications, and other helpful instructor documents.',
      },
      {
        path: documentsPath,
        targetId: 'documents-upload-widget',
        title: 'Upload Documents',
        description: 'Use this area to add instructor documents without leaving the portal.',
      },
      {
        path: documentsPath,
        targetId: 'documents-sections-widget',
        title: 'Document Sections',
        description: 'Your documents are organized into Insurance, Certifications, and Other so they are easy to review.',
      },
      {
        path: dashboardPath,
        targetId: 'nav-profile',
        title: 'Profile',
        description: 'Keep your contact details, class preferences, travel range, and outreach preference current.',
      },
      {
        path: profilePath,
        targetId: 'profile-details-widget',
        title: 'Profile Details',
        description: 'Update your profile photo, address, travel distance, class types, and communication preference here.',
      },
    ];

    if (stripeSetupRequired) {
      steps.push({
        path: location.pathname,
        targetId: 'nav-alerts',
        title: 'Setup Alerts',
        description: 'Important setup reminders appear here, including Stripe payout steps when they need your attention.',
      });
    }

    steps.push(
      {
        path: location.pathname,
        targetId: 'nav-theme-toggle',
        title: 'Dark Mode',
        description: 'Switch between light and dark mode whenever you like.',
      },
      {
        path: location.pathname,
        targetId: 'nav-help',
        title: 'Restart This Tour',
        description: 'You can bring this walkthrough back anytime from this help button.',
      },
    );

    return steps;
  }, [location.pathname, stripeSetupRequired]);

  const steps = useMemo(() => getAllSteps(), [getAllSteps]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileViewport(isMobileTourViewport());
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!isMobileViewport) return undefined;

    setShowWelcome(false);
    setShowTutorial(false);
    broadcastTourTarget('');
    return undefined;
  }, [broadcastTourTarget, isMobileViewport]);

  useEffect(() => {
    if (!onboardingComplete || isMobileViewport) return undefined;

    const restartTutorial = () => {
      if (isMobileTourViewport()) {
        setShowWelcome(false);
        setShowTutorial(false);
        broadcastTourTarget('');
        return;
      }

      setCurrentPage('welcome');
      setShowTutorial(false);
      setShowWelcome(true);
    };

    window.addEventListener('restartTutorial', restartTutorial);

    if (!localStorage.getItem(getTourStorageKey(userDocId))) {
      const timeoutId = window.setTimeout(() => {
        setCurrentPage('welcome');
        setShowWelcome(true);
      }, 500);

      return () => {
        window.clearTimeout(timeoutId);
        window.removeEventListener('restartTutorial', restartTutorial);
        broadcastTourTarget('');
      };
    }

    return () => {
      window.removeEventListener('restartTutorial', restartTutorial);
      broadcastTourTarget('');
    };
  }, [broadcastTourTarget, isMobileViewport, onboardingComplete, userDocId]);

  const handleWelcomeNext = () => {
    if (isMobileTourViewport()) return;

    setShowWelcome(false);
    navigateToStepPath(dashboardPath);
    broadcastTourTarget(steps[0]?.targetId || '');
    setTimeout(() => {
      setShowTutorial(true);
    }, 500);
  };

  const markTourSeen = () => {
    if (userDocId) {
      localStorage.setItem(getTourStorageKey(userDocId), 'true');
    }
  };

  const handleSkipAll = () => {
    setShowWelcome(false);
    setShowTutorial(false);
    broadcastTourTarget('');
    markTourSeen();
  };

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    broadcastTourTarget('');
    setCurrentPage('complete');
    setShowWelcome(true);
  };

  const handleComplete = () => {
    setShowWelcome(false);
    broadcastTourTarget('');
    markTourSeen();
    navigateToStepPath(dashboardPath);
  };

  const handleStepChange = (stepIndex) => {
    const nextStep = steps[stepIndex + 1];
    navigateToStepPath(nextStep?.path);
    broadcastTourTarget(nextStep?.targetId || '');
  };

  const handleStepChangePrevious = (stepIndex) => {
    const previousStep = steps[stepIndex - 1];
    navigateToStepPath(previousStep?.path);
    broadcastTourTarget(previousStep?.targetId || '');
  };

  if (!onboardingComplete || isMobileViewport) return null;

  return (
    <>
      <Modal
        isOpen={showWelcome}
        onClose={currentPage === 'complete' ? handleComplete : handleSkipAll}
        size="xl"
        closeOnOverlayClick={false}
      >
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
        <ModalContent bg={bg}>
          {currentPage === 'welcome' ? (
            <>
              <ModalHeader>
                <VStack align="start" spacing={4}>
                  <Flex align="center" gap={3} w="100%">
                    <Image
                      src={logoWhite}
                      alt="Fit Group USA"
                      h="50px"
                    />
                  </Flex>
                  <Box>
                    <Text fontSize="2xl" fontWeight="700" color={textColor}>
                      Welcome to Your Instructor Portal
                    </Text>
                    <Text fontSize="md" fontWeight="400" color={textColorSecondary}>
                      Your home for assigned classes, completion forms, nearby opportunities, payouts, and documents.
                    </Text>
                  </Box>
                </VStack>
              </ModalHeader>
              <ModalBody>
                <VStack align="stretch" spacing={5}>
                  <Text color={textColor} fontSize="md" lineHeight="tall">
                    The Fit Group USA Instructor Portal helps you manage your schedule, claim classes, complete class forms, and keep payout details organized.
                  </Text>

                  <VStack align="stretch" spacing={3}>
                    <FeatureItem
                      icon={MdCalendarToday}
                      title="Manage Assigned Classes"
                      description="See upcoming classes, past classes, and any completion forms that need your attention."
                      iconBg={iconBg}
                      brandColor={brandColor}
                    />
                    <FeatureItem
                      icon={MdLocationOn}
                      title="Find Nearby Opportunities"
                      description="Browse open classes around your travel area and claim the right fit."
                      iconBg={iconBg}
                      brandColor={brandColor}
                    />
                    <FeatureItem
                      icon={MdPayments}
                      title="Track Payouts"
                      description="Review payment method details, Stripe balance, and payout history."
                      iconBg={iconBg}
                      brandColor={brandColor}
                    />
                    <FeatureItem
                      icon={MdDescription}
                      title="Upload Documents"
                      description="Keep insurance, certifications, and other instructor documents handy."
                      iconBg={iconBg}
                      brandColor={brandColor}
                    />
                  </VStack>

                  <Box
                    bg={iconBg}
                    p={4}
                    borderRadius="12px"
                    borderLeft="4px solid"
                    borderColor={brandColor}
                  >
                    <Text fontSize="sm" color={textColorSecondary}>
                      <strong>Let&apos;s take a quick tour</strong> so you know where everything lives.
                    </Text>
                  </Box>
                </VStack>
              </ModalBody>
              <ModalFooter>
                <HStack spacing={3}>
                  <Button variant="ghost" onClick={handleSkipAll}>
                    Skip Tour
                  </Button>
                  <Button colorScheme="brand" onClick={handleWelcomeNext}>
                    Start Tour
                  </Button>
                </HStack>
              </ModalFooter>
            </>
          ) : currentPage === 'complete' ? (
            <>
              <ModalHeader>
                <VStack align="start" spacing={2}>
                  <Flex align="center" gap={3}>
                    <Box
                      bg="green.500"
                      p={3}
                      borderRadius="12px"
                      color="white"
                    >
                      <Icon as={MdCheckCircle} w={6} h={6} />
                    </Box>
                    <Box>
                      <Text fontSize="2xl" fontWeight="700" color={textColor}>
                        You&apos;re All Set!
                      </Text>
                      <Text fontSize="md" fontWeight="400" color={textColorSecondary}>
                        Ready to use your instructor portal
                      </Text>
                    </Box>
                  </Flex>
                </VStack>
              </ModalHeader>
              <ModalBody>
                <VStack align="stretch" spacing={5}>
                  <Text color={textColor} fontSize="md" lineHeight="tall">
                    You&apos;ve completed the tour. Your classes, opportunities, documents, and payouts are all ready when you need them.
                  </Text>

                  <Box
                    bg={iconBg}
                    p={4}
                    borderRadius="12px"
                  >
                    <VStack align="stretch" spacing={3}>
                      <Flex align="center" gap={3}>
                        <Icon as={MdHelp} color={brandColor} w={5} h={5} />
                        <Box flex="1">
                          <Text fontSize="sm" fontWeight="600" color={textColor}>
                            Need the tour again?
                          </Text>
                          <Text fontSize="sm" color={textColorSecondary}>
                            Use the help icon in the navbar to restart this walkthrough anytime.
                          </Text>
                        </Box>
                      </Flex>
                      <Flex align="center" gap={3}>
                        <Icon as={MdNotifications} color={brandColor} w={5} h={5} />
                        <Box flex="1">
                          <Text fontSize="sm" fontWeight="600" color={textColor}>
                            Watch for setup alerts
                          </Text>
                          <Text fontSize="sm" color={textColorSecondary}>
                            Important account or payout reminders appear in the top navigation.
                          </Text>
                        </Box>
                      </Flex>
                    </VStack>
                  </Box>

                  <Text fontSize="sm" color={textColorSecondary} fontStyle="italic">
                    Empty sections are normal when you do not have assigned classes, overdue forms, or open opportunities yet.
                  </Text>
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button colorScheme="brand" onClick={handleComplete}>
                  Get Started
                </Button>
              </ModalFooter>
            </>
          ) : null}
        </ModalContent>
      </Modal>

      <TutorialOverlay
        steps={steps}
        isActive={showTutorial}
        onComplete={handleTutorialComplete}
        onSkip={handleSkipAll}
        onNext={handleStepChange}
        onPrevious={handleStepChangePrevious}
      />
    </>
  );
}
